import { Activity } from '@shared/models/design/activities';
import {
  ActivityType,
  activityTypes,
} from '../../../models/design/activities/activity-type.model';
import {
  isResidentialProgramType,
  isSchoolProgramType,
} from '../../../models/design/activities/properties';
import {
  GenericValidationError,
  ValidatedValue,
  ValidationResult,
} from '../../../validation/validation.models';
import { guards } from '@shared/helpers/typeguards.helpers';

export const ActivityValidationErrors = {
  ACTIVITY_TYPE_INVALID: 'Activity has invalid type',
  ACTIVITY_PROPERTIES_INVALID: 'Activity has invalid properties',
  ACTIVITY_RESIDENTIAL_PROGRAM_TYPE_INVALID:
    'Residential activity has invalid program_type',
  ACTIVITY_SCHOOL_PROGRAM_TYPE_INVALID:
    'School activity has invalid program_type',
} as const;

/**
 * Check if an activity is valid
 * @param activity Activity to validate
 * @returns ValidationResult
 */
export const isValidActivity = (activity: Activity): ValidationResult => {
  // Type must be one of ActivityType
  if (!activityTypes.includes(activity.type)) {
    return new GenericValidationError(
      ActivityValidationErrors.ACTIVITY_TYPE_INVALID,
    );
  }
  // Basic property structure validation (ids uniqueness checked elsewhere)
  if (activity.properties && !guards.is.object(activity.properties)) {
    return new GenericValidationError(
      ActivityValidationErrors.ACTIVITY_PROPERTIES_INVALID,
    );
  }

  if (activity.type === ActivityType.Residential) {
    if (!isResidentialProgramType(activity.properties.program_type)) {
      return new GenericValidationError(
        ActivityValidationErrors.ACTIVITY_RESIDENTIAL_PROGRAM_TYPE_INVALID,
      );
    }
  }

  if (activity.type === ActivityType.School) {
    if (!isSchoolProgramType(activity.properties.program_type)) {
      return new GenericValidationError(
        ActivityValidationErrors.ACTIVITY_SCHOOL_PROGRAM_TYPE_INVALID,
      );
    }
  }

  return true;
};

/**
 * Validate an activity
 * @param activity Activity to validate
 * @returns Validated item if valid, otherwise the error
 */
export const validateActivity = <T extends Activity>(
  activity: T,
): ValidatedValue<T> => {
  const valid = isValidActivity(activity);
  return guards.is.error(valid) ? valid : activity;
};
